
import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/auth';
import { Subscription } from './types';
import { useSubscriptionFetch } from './fetch/useSubscriptionFetch';

interface UseSubscriptionFetchingProps {
  setIsLoading: (isLoading: boolean) => void;
  setIsSubscribed: (isSubscribed: boolean) => void;
  setCurrentSubscription: (subscription: Subscription | null) => void;
  setLastChecked: (date: Date | null) => void;
  setIsRetrying: (isRetrying: boolean) => void;
  isRefreshingRef: React.MutableRefObject<boolean>;
  isMountedRef: React.MutableRefObject<boolean>;
}

export function useSubscriptionFetching({
  setIsLoading,
  setIsSubscribed,
  setCurrentSubscription,
  setLastChecked,
  setIsRetrying,
  isRefreshingRef,
  isMountedRef
}: UseSubscriptionFetchingProps) {
  const { user } = useAuth();
  const timeoutRef = useRef<number | null>(null);
  const fetchInProgressRef = useRef(false);
  const debounceTimeoutRef = useRef<number | null>(null);
  const lastFetchTimeRef = useRef<number>(0);
  
  // Get the subscription fetch functionality
  const { fetchSubscriptionStatus, cleanupTimeouts } = useSubscriptionFetch({
    userId: user?.id,
    setIsLoading,
    setIsSubscribed,
    setCurrentSubscription,
    setLastChecked,
    setIsRetrying,
    isRefreshingRef,
    isMountedRef,
    fetchInProgressRef,
    debounceTimeoutRef,
    lastFetchTimeRef,
    timeoutRef
  });
  
  // Setup cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupTimeouts();
      fetchInProgressRef.current = false;
    };
  }, [cleanupTimeouts]);

  return {
    fetchSubscriptionStatus,
    cleanupTimeouts
  };
}
